// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { ProductTypesSelectProps, SelectType } from 'interfaces';
import { ProductTypesPayload, useFindAllProductTypeSelectLazyQuery } from 'generated/graphql';

const ProductTypesSelect: FC<ProductTypesSelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  margin = 'dense',
  isClearable = false,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ProductTypesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return options?.map<SelectType>((item) => {
        const { id, name, isActive } = item || {};

        return {
          value: id,
          name: name || '',
          isActive,
        };
      });
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllProductTypeSelectLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllProductType } = data;
      const { data: productTypes, response, pagination } = findAllProductType || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((productTypes as ProductTypesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllProductTypeInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(typeof isActive === 'boolean' && { isActive }),
          },
        },
      });
    },
    [fetchSelectorData, isActive],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default ProductTypesSelect;
